
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Stack, 
  Paper, 
  CircularProgress, 
  Alert,
  Tabs,
  Tab,
  IconButton,
  Button,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Checkbox,
  ListItemText,
  Slider,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import pathfinderApi from '../services/pathfinderApi';
import geofenceApi from '../services/geofenceApi';
import NetworkGraph from '../components/analysis/NetworkGraph';
import { useTracerState } from '../contexts/TracerContext';
import { useDateRange } from '../contexts/DateRangeContext';
import { useTracerStore } from '../stores/tracerStore';
import { useSettings } from '../contexts/SettingsContext';
import { getColorScheme } from '../theme/colorSchemes';
import { 
  findProximityMatches, 
  generateLinkGraph, 
  filterByDateRange,
  findSharedLocations,
  generatePolActivityGraph,
  enrichLocationsWithPlaceNames,
  enrichDwellNodesWithPlaceNames,
} from '../utils/proximityAnalysis';
import type { DwellLocation } from '../utils/dwellDetection';

interface GraphTab {
  id: string;
  label: string;
  nodes: any[];
  links: any[];
  isOriginal: boolean;
}

const LinkAnalysisPage: React.FC = () => {
  // Get data from TracerContext (loaded CSV/GeoNeutron data)
  const { state: tracerState, updateState: _ } = useTracerState();
  const dataPoints = tracerState.dataPoints;
  
  // Get global date range filter from main app (AreaSearch Date Range Filter)
  const { startDate: globalStartDate, endDate: globalEndDate } = useDateRange();
  
  // Get user visualization settings for link analysis appearance
  const { settings } = useSettings();
  const linkAnalysisSettings = settings.visualization.linkAnalysis;
  
  // Get theme colors from user's appearance settings
  const colorScheme = getColorScheme(settings.appearance.colorScheme);
  const themeColors = colorScheme.colors;

  // View mode: 'proximity' for co-location analysis, 'pol-activity' for PoL activity (dwells + shared locations)
  const [viewMode, setViewMode] = useState<'proximity' | 'pol-activity'>('proximity');
  
  // Get dwell locations from main app
  const dwellLocations = tracerState.dwellLocations || [];
  
  // Filter state
  const [proximityDistance, setProximityDistance] = useState(50); // meters
  const [timeWindow, setTimeWindow] = useState(300); // seconds (5 minutes)
  
  // Shared locations view parameters
  const [clusterRadius, setClusterRadius] = useState(100); // meters - radius to consider same location
  const [minSharedLocations, setMinSharedLocations] = useState(1); // minimum shared locations to show link
  
  // Date range state - initialized from global date range filter (main app's Date Range Filter)
  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>(() => {
    // Priority 1: Use global date range filter from main app
    if (globalStartDate && globalEndDate) {
      return { start: globalStartDate, end: globalEndDate };
    }
    // Priority 2: Fall back to tracerState.timeRange
    if (tracerState.timeRange) {
      return { start: tracerState.timeRange.start, end: tracerState.timeRange.end };
    }
    return { start: null, end: null };
  });

  // Sync date range with global Date Range Filter from main app (AreaSearch)
  useEffect(() => {
    if (globalStartDate && globalEndDate) {
      setDateRange({ start: globalStartDate, end: globalEndDate });
      return;
    }
    if (tracerState.timeRange) {
      setDateRange({ start: tracerState.timeRange.start, end: tracerState.timeRange.end });
    }
  }, [globalStartDate, globalEndDate, tracerState.timeRange]);
  
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedLocationCategories, setSelectedLocationCategories] = useState<string[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  
  // Tab management state (graph subset tabs)
  const [tabs, setTabs] = useState<GraphTab[]>([
    { id: 'original', label: 'Original', nodes: [], links: [], isOriginal: true }
  ]);
  const [activeTab, setActiveTab] = useState<string>('original');
  const [selectedNodesForExport, setSelectedNodesForExport] = useState<any[]>([]);
  const [selectionModeEnabled, setSelectionModeEnabled] = useState<boolean>(false);
  
  // Data state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEnrichingLocations, setIsEnrichingLocations] = useState(false);
  
  // Use shared enablePlaceNames from Zustand store (persists + syncs with PoL Analysis)
  const enablePlaceNames = useTracerStore((state) => state.enablePlaceNames);
  const setEnablePlaceNames = useTracerStore((state) => state.setEnablePlaceNames);
  
  
  // Dialog state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameTabId, setRenameTabId] = useState<string>('');
  const [renameTabLabel, setRenameTabLabel] = useState('');
  
  // Note dialog state
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteDialogTitle, setNoteDialogTitle] = useState('');
  const [noteDialogContent, setNoteDialogContent] = useState('');
  const [noteDialogTarget, setNoteDialogTarget] = useState<{
    type: 'device' | 'location' | 'geofence';
    deviceId?: string;
    location?: { lat: number; lng: number; name: string };
    watchlistId?: number;
    geofenceId?: number;
  } | null>(null);
  const [noteDialogLoading, setNoteDialogLoading] = useState(false);
  
  // Snackbar state for feedback
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  
  // Help dialog state
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  
  // Get current tab data
  const currentTab = tabs.find(tab => tab.id === activeTab) || tabs[0];

  // Extract unique entity IDs from nodes (only device types, not phone numbers)
  const availableEntityIds = useMemo(() => {
    const entityIds = new Set<string>();
    currentTab.nodes.forEach((node: any) => {
      // Only include device nodes (not phone numbers)
      if (node.type === 'device' && node.id && !node.id.startsWith('phone:')) {
        entityIds.add(node.id);
      }
    });
    return Array.from(entityIds).sort();
  }, [currentTab.nodes]);

  // Extract unique locations from nodes
  const availableLocations = useMemo(() => {
    const locations = new Map<string, string>(); // id -> label
    currentTab.nodes.forEach((node: any) => {
      if (node.type === 'location' && node.id) {
        const label = node.place_name || node.label || node.id;
        locations.set(node.id, label);
      }
    });
    return Array.from(locations.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, label]) => ({ id, label }));
  }, [currentTab.nodes]);

  // Extract unique location categories from geocoded location nodes
  // Categories come from Mapbox geocoding (place_type: poi, address, neighborhood, etc.)
  const availableLocationCategories = useMemo(() => {
    const categories = new Set<string>();
    
    // From location nodes (geocoded place_type from Mapbox)
    currentTab.nodes.forEach((node: any) => {
      if (node.type === 'location' && node.place_type) {
        categories.add(node.place_type);
      }
    });
    
    return Array.from(categories).filter(c => c && c.trim()).sort();
  }, [currentTab.nodes]);

  // Filter nodes and links based on selected entities, locations, and categories
  const filteredData = useMemo(() => {
    const hasEntityFilter = selectedEntityIds.length > 0;
    const hasLocationFilter = selectedLocations.length > 0;
    const hasCategoryFilter = selectedLocationCategories.length > 0;
    const hasAnyFilter = hasEntityFilter || hasLocationFilter || hasCategoryFilter;
    
    if (!hasAnyFilter && expandedNodes.size === 0) {
      return {
        nodes: currentTab.nodes,
        links: currentTab.links
      };
    }

    // Build set of valid location IDs based on filters
    const validLocationIds = new Set<string>();
    
    // Add locations matching selected categories
    if (hasCategoryFilter) {
      currentTab.nodes.forEach((node: any) => {
        if (node.type === 'location' && node.place_type && selectedLocationCategories.includes(node.place_type)) {
          validLocationIds.add(node.id);
        }
      });
    }

    // Add specific selected locations
    if (hasLocationFilter) {
      selectedLocations.forEach(id => validLocationIds.add(id));
    }

    // Track nodes that should be included
    const connectedNodeIds = new Set<string>();
    
    // Add all selected entities
    if (hasEntityFilter) {
    selectedEntityIds.forEach(id => connectedNodeIds.add(id));
    }

    // Process links to find valid connections based on filters
    currentTab.links.forEach((link: any) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target?.id;

      // Check if either node is expanded - if so, always include this link
      const isExpandedLink = expandedNodes.has(sourceId) || expandedNodes.has(targetId);
      
      if (isExpandedLink) {
        connectedNodeIds.add(sourceId);
        connectedNodeIds.add(targetId);
        return;
      }

      // Apply filters
      let includeLink = false;

      // Entity filter check (specific devices)
      if (hasEntityFilter) {
        // At least one end must be a selected entity
        const hasSelectedEntity = selectedEntityIds.includes(sourceId) || selectedEntityIds.includes(targetId);
        if (!hasSelectedEntity) {
          return; // Skip this link
        }
        includeLink = true;
      }

      // Location/Category filter check
      if (hasLocationFilter || hasCategoryFilter) {
        // Get node types
        const sourceNode = currentTab.nodes.find((n: any) => n.id === sourceId);
        const targetNode = currentTab.nodes.find((n: any) => n.id === targetId);
        
        const sourceIsLocation = sourceNode?.type === 'location';
        const targetIsLocation = targetNode?.type === 'location';

        // Check if link involves a valid location
        let hasValidLocation = false;
        if (sourceIsLocation && validLocationIds.has(sourceId)) {
          hasValidLocation = true;
        }
        if (targetIsLocation && validLocationIds.has(targetId)) {
          hasValidLocation = true;
        }

        if (!hasValidLocation) {
          return; // Skip this link - no matching location
        }
        includeLink = true;
      }

      // If no filters are active but we're here, include all
      if (!hasAnyFilter) {
        includeLink = true;
      }

      // Add nodes if link should be included
      if (includeLink) {
        connectedNodeIds.add(sourceId);
        connectedNodeIds.add(targetId);
      }
    });

    // Filter nodes to only include connected ones
    const filteredNodes = currentTab.nodes.filter((node: any) => 
      connectedNodeIds.has(node.id)
    );

    // Filter links to only include those between valid nodes
    const filteredLinks = currentTab.links.filter((link: any) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
      return connectedNodeIds.has(sourceId) && connectedNodeIds.has(targetId);
    });

    return {
      nodes: filteredNodes,
      links: filteredLinks
    };
  }, [currentTab.nodes, currentTab.links, selectedEntityIds, selectedLocations, selectedLocationCategories, expandedNodes]);

  // Analyze connections when data or parameters change
  useEffect(() => {
    const analyzeData = async () => {
      if (!dateRange.start || !dateRange.end || dataPoints.length === 0) {
        setIsEnrichingLocations(false); // Ensure spinner is stopped
        setTabs(prevTabs => prevTabs.map(tab => 
          tab.isOriginal 
            ? { ...tab, nodes: [], links: [] }
            : tab
        ));
        return;
      }
      
      setLoading(true);
      setError(null);
      setIsEnrichingLocations(false); // Reset enriching state at start
      
      // Reset filters when analyzing new data
      setSelectedEntityIds([]);
      setSelectedLocations([]);
      setSelectedLocationCategories([]);
      setExpandedNodes(new Set());
      
      try {

        // Filter data by date range
        const filteredData = filterByDateRange(dataPoints, dateRange.start, dateRange.end);

        if (filteredData.length === 0) {
          setError('No data points found in the selected date range');
          setTabs(prevTabs => prevTabs.map(tab => 
            tab.isOriginal 
              ? { ...tab, nodes: [], links: [] }
              : tab
          ));
          setLoading(false);
          return;
        }

        let nodes: any[] = [];
        let links: any[] = [];

        if (viewMode === 'proximity') {
          // Proximity-based co-location analysis
          const matches = findProximityMatches(filteredData, proximityDistance, timeWindow);
          
          if (matches.length === 0) {
            setError(`No co-locations found within ${proximityDistance}m and ${timeWindow}s. Try increasing the thresholds and ensure multiple devices are loaded.`);
            setTabs(prevTabs => prevTabs.map(tab => 
              tab.isOriginal 
                ? { ...tab, nodes: [], links: [] }
                : tab
            ));
            setLoading(false);
            return;
          }

          const result = generateLinkGraph(matches);
          nodes = result.nodes;
          links = result.links;
        } else {
          // PoL Activity analysis - includes both shared locations AND detected dwells
          let sharedLocationAnalysis = findSharedLocations(filteredData, clusterRadius);

          // Enrich with real place names from Mapbox (async) - only if enabled
          if (enablePlaceNames && (sharedLocationAnalysis.clusters.length > 0 || dwellLocations.length > 0)) {
            setIsEnrichingLocations(true);
            try {
              sharedLocationAnalysis = await enrichLocationsWithPlaceNames(sharedLocationAnalysis);
            } catch {
              // Continue with coordinate-based labels
            } finally {
              setIsEnrichingLocations(false);
            }
          }

          // Generate PoL Activity graph that includes:
          // 1. All detected dwell locations (even single-device)
          // 2. Shared location clusters between devices
          // 3. Links from devices to their dwell sites
          const result = generatePolActivityGraph(
            sharedLocationAnalysis, 
            dwellLocations as DwellLocation[],
            minSharedLocations,
            clusterRadius
          );
          nodes = result.nodes;
          links = result.links;
          
          // Enrich dwell nodes with place names (if enabled)
          if (enablePlaceNames && nodes.length > 0) {
            try {
              console.log('[LinkAnalysis] Enriching dwell nodes with place names...');
              nodes = await enrichDwellNodesWithPlaceNames(nodes);
              console.log('[LinkAnalysis] Dwell nodes enriched successfully');
            } catch (err) {
              console.warn('[LinkAnalysis] Failed to enrich dwell nodes:', err);
              // Continue with coordinate-based labels
            }
          }
          
          if (nodes.length === 0) {
            setError(`No PoL activity found. Ensure devices are loaded and dwells have been detected in the main analysis.`);
            setTabs(prevTabs => prevTabs.map(tab => 
              tab.isOriginal 
                ? { ...tab, nodes: [], links: [] }
                : tab
            ));
            setLoading(false);
            return;
          }
        }

        // Update the original tab with new data
        setTabs(prevTabs => prevTabs.map(tab => 
          tab.isOriginal 
            ? { ...tab, nodes, links }
            : tab
        ));
        
      } catch (error: any) {
        setError(error.message || 'Failed to analyze data');
        setTabs(prevTabs => prevTabs.map(tab => 
          tab.isOriginal 
            ? { ...tab, nodes: [], links: [] }
            : tab
        ));
      } finally {
        setLoading(false);
        setIsEnrichingLocations(false); // Ensure enriching spinner is always stopped
      }
    };

    analyzeData();
  }, [dataPoints, proximityDistance, timeWindow, dateRange, viewMode, clusterRadius, minSharedLocations, enablePlaceNames, dwellLocations]);

  const handleNodeClick = (node: any) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(node.id)) {
        newSet.delete(node.id);
      } else {
        newSet.add(node.id);
      }
      return newSet;
    });
  };

  const handleClearExpansions = () => {
    setExpandedNodes(new Set());
  };

  const handleSelectionChange = (nodes: any[], autoExport: boolean = false) => {
    setSelectedNodesForExport(nodes);
    
    // Auto-export if requested (e.g., from box selection)
    if (autoExport && nodes.length > 0) {
      // Exit selection mode immediately
      setSelectionModeEnabled(false);
      handleExportToNewTab(nodes);
    }
  };

  const handleExportToNewTab = (nodesToExport?: any[], includeConnections: boolean = true) => {
    const selectedNodes = nodesToExport || selectedNodesForExport;
    if (selectedNodes.length === 0) return;

    // Helper to get ID from link source/target
    const getLinkNodeId = (nodeRef: any): string => {
      if (typeof nodeRef === 'string') return nodeRef;
      if (nodeRef && typeof nodeRef === 'object') return nodeRef.id || '';
      return '';
    };

    // Get IDs of selected nodes
    const selectedIds = new Set(selectedNodes.map((n: any) => n.id));
    
    // If includeConnections is true, find all connected nodes too
    const allNodeIds = new Set(selectedIds);
    
    if (includeConnections) {
      // Find all nodes connected to selected nodes
      currentTab.links.forEach((link: any) => {
        const sourceId = getLinkNodeId(link.source);
        const targetId = getLinkNodeId(link.target);
        
        if (selectedIds.has(sourceId)) {
          allNodeIds.add(targetId);
        }
        if (selectedIds.has(targetId)) {
          allNodeIds.add(sourceId);
        }
      });
    }
    
    // Get all node objects
    const allNodes = currentTab.nodes.filter((n: any) => allNodeIds.has(n.id));
    
    // Find ALL links between the nodes in our set
    const filteredLinks = currentTab.links
      .filter((link: any) => {
        const sourceId = getLinkNodeId(link.source);
        const targetId = getLinkNodeId(link.target);
        return allNodeIds.has(sourceId) && allNodeIds.has(targetId);
      })
      .map((link: any) => ({
        ...link,
        source: getLinkNodeId(link.source),
        target: getLinkNodeId(link.target)
      }));

    // Create label based on selected nodes
    const label = selectedNodes.length === 1 
      ? `${(selectedNodes[0].label || selectedNodes[0].id).substring(0, 12)}... (${allNodes.length})`
      : `${selectedNodes.length} nodes (${allNodes.length})`;

    // Create new tab
    const newTabId = `tab-${Date.now()}`;
    const newTab: GraphTab = {
      id: newTabId,
      label: label,
      nodes: allNodes,
      links: filteredLinks,
      isOriginal: false,
    };

    setTabs([...tabs, newTab]);
    setActiveTab(newTabId);
    setSelectedNodesForExport([]);
    // Ensure selection mode is off (already set in auto-export, but just in case)
    setSelectionModeEnabled(false);
  };

  // Handle exporting a node and its connections from right-click context menu
  const handleExportSubgraph = (subgraphNodes: any[], subgraphLinks: any[], centerNode: any) => {
    if (subgraphNodes.length === 0) return;

    // Get a readable label for the center node
    const centerLabel = centerNode.label || centerNode.id;
    const shortLabel = centerLabel.length > 15 ? centerLabel.substring(0, 12) + '...' : centerLabel;

    // Create new tab
    const newTabId = `tab-${Date.now()}`;
    const newTab: GraphTab = {
      id: newTabId,
      label: `📊 ${shortLabel} (${subgraphNodes.length})`,
      nodes: subgraphNodes,
      links: subgraphLinks,
      isOriginal: false,
    };

    setTabs([...tabs, newTab]);
    setActiveTab(newTabId);
  };

  // Handle saving a device to watchlist
  const handleSaveDevice = useCallback(async (deviceId: string, deviceLabel: string) => {
    try {
      // Strip "GN-" prefix if present - GeoNeutron API only accepts pure UUIDs
      const cleanDeviceId = deviceId.startsWith('GN-') ? deviceId.substring(3) : deviceId;
      const cleanLabel = deviceLabel.startsWith('GN-') ? deviceLabel.substring(3) : deviceLabel;
      
      await pathfinderApi.addToWatchlist(
        cleanDeviceId, 
        cleanLabel,
        '', // notes
        ['link-analysis'], // tags
        undefined // courseId
      );
      
      // Dispatch refresh event so AreaSearch updates its saved devices list
      window.dispatchEvent(new CustomEvent('refresh-watchlist'));
      
      setSnackbarMessage(`✅ Device ${cleanLabel} saved to watchlist!`);
      setSnackbarOpen(true);
    } catch (error: any) {
      // Check if device already exists
      if (error.response?.status === 400 || error.message?.includes('already')) {
        setSnackbarMessage(`ℹ️ Device already in watchlist`);
      } else {
        setSnackbarMessage(`❌ Failed to save device: ${error.message}`);
      }
      setSnackbarOpen(true);
    }
  }, []);

  // Handle adding a note to a device
  const handleAddDeviceNote = useCallback(async (deviceId: string, deviceLabel: string) => {
    try {
      let watchlistId: number | undefined;
      
      // Strip "GN-" prefix if present - GeoNeutron API only accepts pure UUIDs
      const cleanDeviceId = deviceId.startsWith('GN-') ? deviceId.substring(3) : deviceId;
      const cleanLabel = deviceLabel.startsWith('GN-') ? deviceLabel.substring(3) : deviceLabel;
      
      // First, try to add the device to the watchlist
      try {
        const watchlistEntry = await pathfinderApi.addToWatchlist(
          cleanDeviceId, 
          cleanLabel,
          '', // notes
          ['link-analysis'], // tags
          undefined // courseId
        );
        watchlistId = watchlistEntry.id;
      } catch (addError: any) {
        // If device already exists (400 error), fetch the existing watchlist entry
        if (addError.response?.status === 400 || addError.message?.includes('already')) {
          const watchlist = await pathfinderApi.getWatchlist();
          // Search for both clean ID and original ID (in case it was saved with prefix)
          const existing = watchlist.find((w: any) => 
            w.registration_id === cleanDeviceId || w.registration_id === deviceId
          );
          if (existing) {
            watchlistId = existing.id;
          }
        }
        
        if (!watchlistId) {
          throw addError;
        }
      }
      
      // Dispatch refresh event so AreaSearch updates its saved devices list
      window.dispatchEvent(new CustomEvent('refresh-watchlist'));
      
      // Open note dialog with watchlist context
      setNoteDialogTarget({
        type: 'device',
        deviceId,
        watchlistId
      });
      setNoteDialogTitle(`Add Note for ${deviceLabel}`);
      setNoteDialogContent('');
      setNoteDialogOpen(true);
    } catch (error: any) {
      setSnackbarMessage(`Failed to prepare note: ${error.response?.data?.detail || error.message}`);
      setSnackbarOpen(true);
    }
  }, []);

  // Handle saving a location as a geofence
  const handleSaveAsGeofence = useCallback(async (location: { lat: number; lng: number; name: string; placeType?: string }) => {
    try {
      // Create a circular geofence polygon (approximation)
      const radiusMeters = 100; // Default 100m radius
      const numPoints = 32;
      const coordinates: number[][] = [];
      
      for (let i = 0; i <= numPoints; i++) {
        const angle = (i / numPoints) * 2 * Math.PI;
        // Approximate meters to degrees (rough estimate)
        const latOffset = (radiusMeters / 111320) * Math.cos(angle);
        const lngOffset = (radiusMeters / (111320 * Math.cos(location.lat * Math.PI / 180))) * Math.sin(angle);
        coordinates.push([location.lng + lngOffset, location.lat + latOffset]);
      }
      
      const geofence = await geofenceApi.createGeofence({
        name: location.name,
        description: `Created from Link Analysis - ${location.placeType || 'location'}`,
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates]
        }
      });
      
      setSnackbarMessage(`✅ Geofence "${location.name}" saved successfully!`);
      setSnackbarOpen(true);
      
      // Optionally open note dialog for the new geofence
      setNoteDialogTarget({
        type: 'geofence',
        location,
        geofenceId: geofence.id
      });
      setNoteDialogTitle(`Add Note for Geofence: ${location.name}`);
      setNoteDialogContent('');
      setNoteDialogOpen(true);
    } catch (error: any) {
      setSnackbarMessage(`❌ Failed to save geofence: ${error.message}`);
      setSnackbarOpen(true);
    }
  }, []);

  // Handle adding a note to a location (requires geofence first)
  const handleAddLocationNote = useCallback(async (location: { lat: number; lng: number; name: string }) => {
    // For location notes, we need to create a geofence first, then add notes to it
    // Or show a simplified dialog
    setNoteDialogTarget({
      type: 'location',
      location
    });
    setNoteDialogTitle(`Add Note for Location: ${location.name}`);
    setNoteDialogContent('');
    setNoteDialogOpen(true);
  }, []);

  // Handle saving the note
  const handleSaveNote = useCallback(async () => {
    if (!noteDialogTarget || !noteDialogContent.trim()) return;
    
    setNoteDialogLoading(true);
    try {
      if (noteDialogTarget.type === 'device' && noteDialogTarget.watchlistId) {
        await pathfinderApi.createNote(noteDialogContent, noteDialogTarget.watchlistId, undefined);
        setSnackbarMessage('✅ Note saved to device successfully!');
      } else if (noteDialogTarget.type === 'geofence' && noteDialogTarget.geofenceId) {
        await pathfinderApi.createNote(noteDialogContent, undefined, noteDialogTarget.geofenceId);
        setSnackbarMessage('✅ Note saved to geofence successfully!');
      } else if (noteDialogTarget.type === 'location' && noteDialogTarget.location) {
        // For standalone location notes, create a geofence first
        const loc = noteDialogTarget.location;
        const radiusMeters = 100;
        const numPoints = 32;
        const coordinates: number[][] = [];
        
        for (let i = 0; i <= numPoints; i++) {
          const angle = (i / numPoints) * 2 * Math.PI;
          const latOffset = (radiusMeters / 111320) * Math.cos(angle);
          const lngOffset = (radiusMeters / (111320 * Math.cos(loc.lat * Math.PI / 180))) * Math.sin(angle);
          coordinates.push([loc.lng + lngOffset, loc.lat + latOffset]);
        }
        
        const geofence = await geofenceApi.createGeofence({
          name: loc.name,
          description: `Created from Link Analysis location note`,
          geometry: { type: 'Polygon', coordinates: [coordinates] }
        });
        
        await pathfinderApi.createNote(noteDialogContent, undefined, geofence.id);
        setSnackbarMessage('✅ Geofence and note saved successfully!');
      }
      
      setNoteDialogOpen(false);
      setNoteDialogContent('');
      setNoteDialogTarget(null);
    } catch (error: any) {
      setSnackbarMessage(`❌ Failed to save note: ${error.message}`);
    } finally {
      setNoteDialogLoading(false);
      setSnackbarOpen(true);
    }
  }, [noteDialogTarget, noteDialogContent]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    setSelectedNodesForExport([]);
    // Exit selection mode when switching tabs
    setSelectionModeEnabled(false);
  };

  const handleCloseTab = (tabId: string) => {
    if (tabs.length <= 1) return; // Don't close last tab
    
    const tab = tabs.find(t => t.id === tabId);
    if (tab?.isOriginal) return; // Don't close original tab
    
    const newTabs = tabs.filter(t => t.id !== tabId);
    setTabs(newTabs);
    
    // Switch to original tab if closing active tab
    if (activeTab === tabId) {
      setActiveTab(newTabs[0].id);
    }
  };

  const handleDuplicateTab = (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    const newTabId = `tab-${Date.now()}`;
    const newTab: GraphTab = {
      id: newTabId,
      label: `${tab.label} (Copy)`,
      nodes: [...tab.nodes],
      links: [...tab.links],
      isOriginal: false,
    };

    setTabs([...tabs, newTab]);
    setActiveTab(newTabId);
  };

  const handleRenameTab = (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    setRenameTabId(tabId);
    setRenameTabLabel(tab.label);
    setRenameDialogOpen(true);
  };

  const handleRenameConfirm = () => {
    setTabs(tabs.map(tab => 
      tab.id === renameTabId 
        ? { ...tab, label: renameTabLabel }
        : tab
    ));
    setRenameDialogOpen(false);
  };

  return (
    <Box sx={{ 
      height: 'calc(100vh - 80px)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Data Source Info */}
      <Alert 
        severity={dataPoints.length > 0 ? "info" : "warning"} 
        sx={{ mb: 1, flexShrink: 0 }}
        action={
          <IconButton
            color="inherit"
            size="small"
            onClick={() => setHelpDialogOpen(true)}
            sx={{ ml: 1 }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        }
      >
        {dataPoints.length > 0 ? (
          <>
            🔗 <strong>{viewMode === 'proximity' ? 'Proximity Co-location' : 'PoL Activity'} Analysis</strong> - Analyzing {dataPoints.length.toLocaleString()} data points 
            {dateRange.start && dateRange.end && (
              <> (filtered: {dateRange.start.toLocaleDateString()} - {dateRange.end.toLocaleDateString()})</>
            )}
          </>
        ) : (
          <>
             <strong>No Data Loaded</strong> - Please load CSV or OSINT API data from the OSINT Explorer tab first
          </>
        )}
      </Alert>

      {/* Filter Controls */}
      <Box sx={{ flexShrink: 0, mb: 0.5 }}>
        <Paper elevation={2} sx={{ p: 1 }}>
          {/* View Mode Toggle */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
              Analysis Mode:
            </Typography>
            <ButtonGroup size="small" variant="outlined">
              <Button
                variant={viewMode === 'proximity' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('proximity')}
                sx={{ textTransform: 'none' }}
              >
                🎯 Proximity Co-location
              </Button>
              <Button
                variant={viewMode === 'pol-activity' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('pol-activity')}
                sx={{ textTransform: 'none' }}
              >
                📍 PoL Activity
              </Button>
            </ButtonGroup>
            
            {/* View-specific parameters */}
            {viewMode === 'pol-activity' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Cluster Radius:
                  </Typography>
                  <Slider
                    value={clusterRadius}
                    onChange={(_, value) => setClusterRadius(value as number)}
                    min={25}
                    max={500}
                    step={25}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v}m`}
                    sx={{ width: 120 }}
                  />
                  <Typography variant="caption" sx={{ minWidth: 45 }}>
                    {clusterRadius}m
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Min Shared:
                  </Typography>
                  <Slider
                    value={minSharedLocations}
                    onChange={(_, value) => setMinSharedLocations(value as number)}
                    min={1}
                    max={10}
                    step={1}
                    valueLabelDisplay="auto"
                    sx={{ width: 80 }}
                  />
                  <Typography variant="caption" sx={{ minWidth: 20 }}>
                    {minSharedLocations}
                  </Typography>
                </Box>
                
                {/* Place Names Toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, pl: 2, borderLeft: '1px solid rgba(255,255,255,0.2)' }}>
                  <Tooltip title="Uses Mapbox API to show real place names instead of coordinates. Slower initial load, but results are cached.">
                    <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                      🗺️ Place Names:
                    </Typography>
                  </Tooltip>
                  {isEnrichingLocations && <CircularProgress size={12} />}
                  <ToggleButtonGroup
                    value={enablePlaceNames ? 'on' : 'off'}
                    exclusive
                    onChange={(_, val) => val && setEnablePlaceNames(val === 'on')}
                    size="small"
                    sx={{ height: 22 }}
                  >
                    <ToggleButton value="off" sx={{ px: 0.75, py: 0, fontSize: '0.65rem' }}>Off</ToggleButton>
                    <ToggleButton value="on" sx={{ px: 0.75, py: 0, fontSize: '0.65rem' }}>On</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </>
            )}
            
            <Tooltip title={viewMode === 'proximity' 
              ? "Shows devices that were physically near each other at the same time" 
              : "Shows device dwell locations and shared activity patterns"
            }>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                ℹ️ {viewMode === 'proximity' 
                  ? 'Devices within distance & time threshold' 
                  : `Dwells: ${dwellLocations.length} | Clusters + shared locations`}
              </Typography>
            </Tooltip>
          </Stack>
          
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={dateRange.start ? dateRange.start.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const newStart = e.target.value ? new Date(e.target.value) : null;
                setDateRange(prev => ({ ...prev, start: newStart }));
              }}
              onClick={(e) => {
                // Make the entire field clickable to open the date picker
                const input = e.currentTarget.querySelector('input');
                if (input) {
                  input.showPicker?.();
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ 
                minWidth: 160,
                cursor: 'pointer',
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1)',
                  cursor: 'pointer',
                  opacity: 0.7,
                  '&:hover': {
                    opacity: 1
                  }
                }
              }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={dateRange.end ? dateRange.end.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const newEnd = e.target.value ? new Date(e.target.value) : null;
                setDateRange(prev => ({ ...prev, end: newEnd }));
              }}
              onClick={(e) => {
                // Make the entire field clickable to open the date picker
                const input = e.currentTarget.querySelector('input');
                if (input) {
                  input.showPicker?.();
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ 
                minWidth: 160,
                cursor: 'pointer',
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1)',
                  cursor: 'pointer',
                  opacity: 0.7,
                  '&:hover': {
                    opacity: 1
                  }
                }
              }}
            />

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Filter by Device</InputLabel>
              <Select
                multiple
                value={selectedEntityIds}
                label="Filter by Device"
                onChange={(e) => setSelectedEntityIds(e.target.value as string[])}
                disabled={availableEntityIds.length === 0}
                renderValue={(selected) => 
                  selected.length === 0 
                    ? <em>All Devices</em>
                    : selected.length === 1
                    ? selected[0]
                    : `${selected.length} devices selected`
                }
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 300 } }
                }}
              >
                {availableEntityIds.map((entityId) => (
                  <MenuItem key={entityId} value={entityId} dense>
                    <Checkbox checked={selectedEntityIds.indexOf(entityId) > -1} size="small" />
                    <ListItemText primary={entityId} primaryTypographyProps={{ variant: 'body2' }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Filter by Location</InputLabel>
              <Select
                multiple
                value={selectedLocations}
                label="Filter by Location"
                onChange={(e) => setSelectedLocations(e.target.value as string[])}
                disabled={availableLocations.length === 0}
                renderValue={(selected) => 
                  selected.length === 0 
                    ? <em>All Locations</em>
                    : selected.length === 1
                    ? availableLocations.find(loc => loc.id === selected[0])?.label || selected[0]
                    : `${selected.length} locations`
                }
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 300 } }
                }}
              >
                {availableLocations.map((location) => (
                  <MenuItem key={location.id} value={location.id} dense>
                    <Checkbox checked={selectedLocations.indexOf(location.id) > -1} size="small" />
                    <ListItemText primary={location.label} primaryTypographyProps={{ variant: 'body2' }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Filter by Category</InputLabel>
              <Select
                multiple
                value={selectedLocationCategories}
                label="Filter by Category"
                onChange={(e) => setSelectedLocationCategories(e.target.value as string[])}
                disabled={availableLocationCategories.length === 0}
                renderValue={(selected) => 
                  selected.length === 0 
                    ? <em>All Categories</em>
                    : selected.length === 1
                    ? selected[0]
                    : `${selected.length} categories`
                }
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 300 } }
                }}
              >
                {availableLocationCategories.map((category) => (
                  <MenuItem key={category} value={category} dense>
                    <Checkbox checked={selectedLocationCategories.indexOf(category) > -1} size="small" />
                    <ListItemText primary={category} primaryTypographyProps={{ variant: 'body2' }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {(loading || isEnrichingLocations) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  {isEnrichingLocations ? 'Fetching place names...' : 'Loading network data...'}
                </Typography>
              </Box>
            )}

            {/* Expanded Nodes Info */}
            {expandedNodes.size > 0 && (
              <Tooltip title="Clear all expanded nodes to return to filtered view">
                <Button
                  size="small"
                  variant="outlined"
                  color="info"
                  onClick={handleClearExpansions}
                  sx={{ ml: 'auto' }}
                >
                  {expandedNodes.size} Node{expandedNodes.size > 1 ? 's' : ''} Expanded
                </Button>
              </Tooltip>
            )}
          </Stack>

          {/* Error Alerts */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Export to New Tab Button */}
          {selectedNodesForExport.length > 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2">
                  <strong>{selectedNodesForExport.length}</strong> nodes selected
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<AddIcon />}
                  onClick={() => handleExportToNewTab()}
                >
                  Export to New Tab
                </Button>
              </Stack>
            </Alert>
          )}
        </Paper>
      </Box>

      {/* Tabs */}
      <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
        <Paper elevation={0} sx={{ px: 1 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {tab.label}
                    {!tab.isOriginal && (
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        <Tooltip title="Rename">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRenameTab(tab.id);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateTab(tab.id);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Close">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloseTab(tab.id);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Paper>
      </Box>

      {/* Network Graph - Takes remaining space */}
      <Box sx={{ 
        flex: 1,
        minHeight: 0,
        overflow: 'hidden'
      }}>
        <NetworkGraph
          nodes={filteredData.nodes}
          links={filteredData.links}
          width={0}
          height={0}
          onNodeClick={handleNodeClick}
          onSelectionChange={handleSelectionChange}
          selectionMode={selectionModeEnabled}
          proximityDistance={proximityDistance}
          onProximityDistanceChange={setProximityDistance}
          timeWindow={timeWindow}
          onTimeWindowChange={setTimeWindow}
          onRefreshAnalysis={() => setDateRange({ ...dateRange })}
          onExportSubgraph={handleExportSubgraph}
          appearanceSettings={linkAnalysisSettings}
          themeColors={themeColors}
          onSaveDevice={handleSaveDevice}
          onAddDeviceNote={handleAddDeviceNote}
          onSaveAsGeofence={handleSaveAsGeofence}
          onAddLocationNote={handleAddLocationNote}
        />
      </Box>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Rename Tab</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tab Name"
            type="text"
            fullWidth
            value={renameTabLabel}
            onChange={(e) => setRenameTabLabel(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleRenameConfirm();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRenameConfirm} variant="contained">Rename</Button>
        </DialogActions>
      </Dialog>

      {/* Note Dialog */}
      <Dialog 
        open={noteDialogOpen} 
        onClose={() => !noteDialogLoading && setNoteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{noteDialogTitle}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Note"
            fullWidth
            multiline
            rows={4}
            value={noteDialogContent}
            onChange={(e) => setNoteDialogContent(e.target.value)}
            disabled={noteDialogLoading}
            placeholder="Enter your note here..."
          />
          {noteDialogTarget?.type === 'location' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              A geofence will be automatically created for this location to save the note.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setNoteDialogOpen(false)} 
            disabled={noteDialogLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveNote} 
            variant="contained" 
            disabled={noteDialogLoading || !noteDialogContent.trim()}
          >
            {noteDialogLoading ? <CircularProgress size={20} /> : 'Save Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Help Dialog */}
      <Dialog 
        open={helpDialogOpen} 
        onClose={() => setHelpDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">📊 Link Analysis Help</Typography>
          <IconButton onClick={() => setHelpDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* Overview */}
          <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
            Overview
          </Typography>
          <Typography variant="body2" paragraph>
            Link Analysis visualizes relationships between devices based on their location data. 
            It helps identify patterns of co-location, shared destinations, and behavioral associations.
          </Typography>

          {/* Analysis Modes */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Analysis Modes
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1 }}>
            🎯 Proximity Co-location
          </Typography>
          <Typography variant="body2" paragraph>
            Finds devices that were <strong>physically near each other at the same time</strong>. 
            Useful for identifying devices traveling together, meeting points, or potential associations.
          </Typography>
          <Typography variant="body2" component="div" sx={{ pl: 2, mb: 2 }}>
            • <strong>Proximity Distance:</strong> Maximum distance (meters) between devices to consider them co-located<br/>
            • <strong>Time Window:</strong> Maximum time difference (seconds) between observations to consider them simultaneous<br/>
            • Links show the number of co-location events and their strength
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1 }}>
            📍 PoL Activity (Pattern of Life)
          </Typography>
          <Typography variant="body2" paragraph>
            Combines <strong>detected dwells</strong> from the main analysis with <strong>shared location clusters</strong>.
            Shows all places devices visited - whether shared or individual.
          </Typography>
          <Typography variant="body2" component="div" sx={{ pl: 2, mb: 2 }}>
            • <strong>Dwell Sites (pink):</strong> Locations where a single device stopped for significant time<br/>
            • <strong>Shared Locations (green):</strong> Places multiple devices visited (even at different times)<br/>
            • <strong>Shared Dwells (orange):</strong> Places where multiple devices dwelled<br/>
            • <strong>Cluster Radius:</strong> Distance to group nearby locations as one site<br/>
            • <strong>Min Shared:</strong> Minimum shared locations needed to show device connections<br/>
            • Links show visit counts and total dwell duration
          </Typography>

          {/* Node Types */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Node Types
          </Typography>
          <Typography variant="body2" component="div" sx={{ pl: 2, mb: 2 }}>
            • <strong>Device Nodes (colored circles):</strong> Individual devices with unique hue per device<br/>
            • <strong>Location Nodes (pink/green/orange):</strong> Geographic locations with coordinates<br/>
            • Node size indicates connection strength (more connections = larger)
          </Typography>

          {/* Interactions */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Interactions
          </Typography>
          <Typography variant="body2" component="div" sx={{ pl: 2, mb: 2 }}>
            • <strong>Click node:</strong> Select and expand to show all connections<br/>
            • <strong>Right-click device:</strong> Add notes (saves to watchlist)<br/>
            • <strong>Right-click location:</strong> Save as geofence or add notes<br/>
            • <strong>Drag nodes:</strong> Reposition for better visualization<br/>
            • <strong>Scroll:</strong> Zoom in/out<br/>
            • <strong>Selection mode:</strong> Click multiple nodes, then export to new tab
          </Typography>

          {/* Filtering */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Filtering
          </Typography>
          <Typography variant="body2" component="div" sx={{ pl: 2, mb: 2 }}>
            • <strong>Date Range:</strong> Filter data to specific time period<br/>
            • <strong>Entity Filter:</strong> Show only specific devices<br/>
            • <strong>Location Filter:</strong> Show only specific locations<br/>
            • <strong>Category Filter:</strong> Filter by place type (restaurant, gas station, etc.)<br/>
            • <strong>Place Names:</strong> Toggle real place names from Mapbox (slower but more readable)
          </Typography>

          {/* Tabs */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Working with Tabs
          </Typography>
          <Typography variant="body2" component="div" sx={{ pl: 2, mb: 2 }}>
            • <strong>Original Tab:</strong> Shows full analysis (cannot be closed)<br/>
            • <strong>Export to Tab:</strong> Select nodes and export subset for focused analysis<br/>
            • <strong>Duplicate Tab:</strong> Copy a tab to explore different filters<br/>
            • <strong>Rename Tab:</strong> Give meaningful names to exported subgraphs
          </Typography>

          {/* Tips */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            💡 Tips
          </Typography>
          <Typography variant="body2" component="div" sx={{ pl: 2 }}>
            • Run dwell detection in the main OSINT Explorer tab first for best PoL Activity results<br/>
            • Start with larger proximity distances and narrow down<br/>
            • Use Place Names toggle sparingly on large datasets (slower)<br/>
            • Export interesting subgraphs to tabs for comparative analysis<br/>
            • Right-click to add notes - they're saved for future reference
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LinkAnalysisPage;
